import React, { useEffect, useRef } from "react";
import "./RejectOrderConfirmation.css";

const RejectOrderConfirmation = ({
  reasonOptions,
  selectedReason,
  customReason,
  onReasonChange,
  onCustomReasonChange,
  error,
  isRejecting,
  isReasonValid,
  onCancel,
  onConfirm,
  title = "Reject this order?",
  warning = "This action is irreversible. The order will be rejected and cannot be restored.",
}) => {
  const selectRef = useRef(null);
  const customReasonRequired = selectedReason === "OTHER";

  useEffect(() => {
    selectRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isRejecting) {
        event.preventDefault();
        onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isRejecting, onCancel]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onConfirm();
  };

  return (
    <div className="reject-confirm-overlay">
      <form
        className="reject-confirm-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reject-confirm-title"
        aria-describedby="reject-confirm-warning"
        onSubmit={handleSubmit}
      >
        <div className="reject-confirm-mark" aria-hidden="true">
          !
        </div>
        <h2 id="reject-confirm-title" className="reject-confirm-title">
          {title}
        </h2>
        <p id="reject-confirm-warning" className="reject-confirm-warning">
          {warning}
        </p>

        <div className="reject-confirm-fields">
          <div className="reject-confirm-field">
            <label htmlFor="reject-reason" className="reject-confirm-label">
              Rejection reason <span aria-hidden="true">*</span>
            </label>
            <select
              ref={selectRef}
              id="reject-reason"
              className="reject-confirm-control"
              value={selectedReason}
              disabled={isRejecting}
              onChange={(event) => onReasonChange(event.target.value)}
              required
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "reject-confirm-error" : undefined}
            >
              <option value="">Choose a reason</option>
              {reasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {customReasonRequired && (
            <div className="reject-confirm-field">
              <label htmlFor="reject-custom-reason" className="reject-confirm-label">
                Custom reason <span aria-hidden="true">*</span>
              </label>
              <textarea
                id="reject-custom-reason"
                className="reject-confirm-control reject-confirm-textarea"
                value={customReason}
                disabled={isRejecting}
                onChange={(event) => onCustomReasonChange(event.target.value)}
                maxLength={500}
                minLength={3}
                required
                aria-invalid={Boolean(error)}
                aria-describedby={`reject-custom-hint${error ? " reject-confirm-error" : ""}`}
              />
              <p id="reject-custom-hint" className="reject-confirm-hint">
                Explain the reason in 3–500 characters.
              </p>
            </div>
          )}
        </div>

        {error && (
          <p id="reject-confirm-error" className="reject-confirm-error" role="alert">
            {error}
          </p>
        )}

        <div className="reject-confirm-actions">
          <button
            type="button"
            className="reject-confirm-cancel"
            onClick={onCancel}
            disabled={isRejecting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="reject-confirm-submit"
            disabled={isRejecting || !isReasonValid}
            aria-busy={isRejecting}
          >
            {isRejecting && <span className="loader kds-btn-loader" aria-hidden="true" />}
            {isRejecting ? "Rejecting…" : "Yes, Reject"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RejectOrderConfirmation;
